import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  signal,
  untracked,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSidenav } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { MediaObserver } from '@ngbracket/ngx-layout';
import {
  AiChatComponent,
  AiChatHistoryComponent,
  AiChatPopoutComponent,
  AiChatSettingsComponent,
  AlloMemoryNoticeService,
  AlloPanelService,
} from '@qtek/libs/ai-chat';
import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import { PermissionsService, PermissionType } from '@qtek/libs/permissions';
import { PopoutService } from '@qtek/libs/popout';
import {
  AiChatStore,
  NewRelationsStore,
  TagManagerStore,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { AiChat, Customer, Tag } from '@qtek/shared/models';
import {
  animationFrameScheduler,
  BehaviorSubject,
  fromEvent,
  Subject,
} from 'rxjs';
import {
  auditTime,
  filter,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { TaskListComponent } from '../task-list/task-list.component';

const AI_CHAT_POPOUT_STORAGE_KEY = 'aiChatPopoutState';
const AI_CHAT_POPOUT_RESTORE_KEY = 'aiChatPopoutRestore';
const AI_CHAT_POPOUT_WINDOW_NAME = 'AiChatPopout';
const AI_CHAT_POPOUT_RESTORE_TTL_MS = 5000;

interface AiChatPopoutState {
  chatId: string | null;
  windowId: string;
  startNew: boolean;
  lazySubscribe: boolean;
}

@Component({
  selector: 'qt-right-sidenav',
  templateUrl: './right-sidenav.component.html',
  styleUrls: ['./right-sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'right-sidenav',
    '[style.width.px]': 'width()',
  },
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    CommonModule,
    TaskListComponent,
    TranslationCoreModule,
    AiChatComponent,
    AiChatHistoryComponent,
    AiChatSettingsComponent,
    MatDividerModule,
  ],
})
export class RightSidenavComponent implements OnDestroy, OnInit, AfterViewInit {
  sidenav = inject(MatSidenav);
  private aiChatStore = inject(AiChatStore);
  private newRelationsStore = inject(NewRelationsStore);
  private tagManagerStore = inject(TagManagerStore);
  private readonly tags$ = toObservable(this.tagManagerStore.tags);
  media = inject(MediaObserver);
  private changeDetectionRef = inject(ChangeDetectorRef);
  private dialogLoader = inject(LazyDialogLoader);
  private readonly alloPanel = inject(AlloPanelService);
  /** SD-2716: composer seed from an in-page "Ask Allo". Prefill only — never sent. */
  readonly askPrompt = this.alloPanel.initialPrompt;

  @ViewChild('resizeHandle') resizeHandle: ElementRef;
  @Input() mini: boolean;
  @Output() closeSidenav = new EventEmitter<void>();
  @Output() widthChange = new EventEmitter<number>();
  @Output() activeTabChange = new EventEmitter<'chat' | 'tasks'>();
  @Output() chatAvailabilityChange = new EventEmitter<boolean>();
  readonly showChat = signal(false);
  readonly showHistory = signal(false);
  /** SD-2096: the Allo settings drop-down (cross-session memory controls). */
  readonly showSettings = signal(false);
  readonly selectedTab = signal<'chat' | 'tasks'>('tasks');
  readonly selectedChatId = signal<string | null>(null);
  readonly chatSessionKey = signal(this.createWindowId());
  readonly lazyChatSubscribe = signal(false);
  width = signal(400);

  // Minimum width of the Allo sidenav so header buttons don't get cut off
  private readonly minWidth = 280;
  private readonly maxWidth = 800;
  private router = inject(Router);
  private noteAllTags$ = new BehaviorSubject<Tag[]>([]);
  private mysid = 'right-sidenav-contacts';
  private readonly destroy$ = new Subject<void>();
  private readonly chatFeature = 'ai-agent';
  private permissions = inject(PermissionsService);
  private popoutService = inject(PopoutService);
  private viewContainerRef = inject(ViewContainerRef);
  private lastChatHistoryMysid: string | null = null;
  private ngZone = inject(NgZone);
  private popoutRestored = false;
  /** SD-3045: the popout session key whose state the docked chat last applied. */
  private followedPopoutWindowId: string | null = null;
  private readonly memoryNotices = inject(AlloMemoryNoticeService);
  /** SD-2096: dot on the settings button while Allo's memory changed unseen. */
  readonly unseenMemoryChange = this.memoryNotices.unseenChange;

  private createWindowId(): string {
    return `rs-ai-${Date.now().toString(16)}-${Math.random()
      .toString(16)
      .slice(2, 10)}`;
  }

  constructor() {
    // Sync width signal changes to output event
    effect(() => {
      const currentWidth = this.width();
      this.widthChange.emit(currentWidth);
    });

    // Initial width emit
    setTimeout(() => {
      this.widthChange.emit(this.width());
    });

    this.tagManagerStore.connect({});
    this.tags$
      .pipe(
        filter(tags => !!tags),
        tap(tags => this.noteAllTags$.next(tags as Tag[])),
        takeUntil(this.destroy$)
      )
      .subscribe();

    effect(() => {
      const chats = this.aiChatStore.chatsWithPreview();
      untracked(() => {
        const normalized = chats.filter((chat): chat is AiChat => !!chat?.id);

        const current = this.selectedChatId();
        if (current && !normalized.some(chat => chat.id === current)) {
          this.selectedChatId.set(null);
        }
      });
    });

    this.permissions
      .isAllowed$(this.chatFeature, PermissionType.View)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAllowed => {
        this.showChat.set(isAllowed);
        this.chatAvailabilityChange.emit(isAllowed);
        if (isAllowed) {
          this.setActiveTab('chat');
          this.requestChatHistoryIfNeeded();
          this.restoreAiChatPopoutIfNeeded();
        }
        if (!isAllowed) {
          this.setActiveTab('tasks');
        }
      });

    fromEvent<MessageEvent>(window, 'message')
      .pipe(
        filter(event => event.origin === window.location.origin),
        filter(event => event.data?.type === 'ai-chat-popout-state'),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        const state = event.data?.state as AiChatPopoutState | undefined;
        if (!state) return;
        this.saveAiChatPopoutState(state);
        if (this.selectedTab() === 'chat') {
          this.applyPopoutState(state);
        }
      });
  }

  ngOnInit(): void {
    this.chatAvailabilityChange.emit(this.showChat());

    // SD-2096: "Review" on the memory-changed toast — open the panel it refers to.
    this.memoryNotices.reviewRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.openSettings());
  }

  ngAfterViewInit(): void {
    this.setupResizeHandle();
  }

  private setupResizeHandle(): void {
    if (!this.resizeHandle?.nativeElement) {
      return;
    }

    const handle = this.resizeHandle.nativeElement;

    // Run event listeners outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      fromEvent<PointerEvent>(handle, 'pointerdown')
        .pipe(
          filter(event => event.button === 0), // Only left mouse button
          switchMap(startEvent => {
            startEvent.preventDefault();
            startEvent.stopPropagation();

            const startX = startEvent.clientX;
            const startWidth = this.width();

            // Create move stream with throttling
            const move$ = fromEvent<PointerEvent>(document, 'pointermove').pipe(
              map(moveEvent => {
                const deltaX = startX - moveEvent.clientX; // Inverted because we're resizing from left
                return Math.max(
                  this.minWidth,
                  Math.min(this.maxWidth, startWidth + deltaX)
                );
              }),
              // Throttle to ~60fps using auditTime - waits for silence then emits latest
              // This ensures smooth updates without overwhelming the CPU
              auditTime(0, animationFrameScheduler),
              // Finalize to ensure cleanup
              finalize(() => {
                if (startEvent.pointerId !== undefined) {
                  handle.releasePointerCapture?.(startEvent.pointerId);
                }
              })
            );

            // Create up stream
            const up$ = fromEvent<PointerEvent>(document, 'pointerup').pipe(
              tap(() => {
                if (startEvent.pointerId !== undefined) {
                  handle.releasePointerCapture?.(startEvent.pointerId);
                }
              })
            );

            // Return move stream until pointer up
            return move$.pipe(takeUntil(up$));
          }),
          takeUntil(this.destroy$)
        )
        .subscribe(newWidth => {
          // Only enter Angular zone when actually updating
          this.ngZone.run(() => {
            this.width.set(newWidth);
          });
        });
    });
  }

  closePanel(): void {
    this.closeSidenav.emit();
  }

  /** The composer has read the seed, so it must not survive into the next chat component. */
  onAskPromptConsumed(): void {
    this.alloPanel.clearPrompt();
  }

  startNewChat(): void {
    if (!this.showChat()) return;
    // The chat component is recreated per session key — a stale "Ask Allo" seed must not
    // re-prefill a thread the reader deliberately started fresh.
    this.alloPanel.clearPrompt();
    this.showHistory.set(false);
    this.showSettings.set(false);
    this.lazyChatSubscribe.set(true);
    this.chatSessionKey.set(this.createWindowId());
    this.selectedChatId.set(null);
    this.setActiveTab('chat');
    this.requestChatHistoryIfNeeded();
  }

  toggleHistory(): void {
    if (!this.showChat()) return;
    this.showHistory.set(!this.showHistory());
    // History and settings are the same drop-down slot under the header — showing both at once
    // would push the transcript off-screen.
    if (this.showHistory()) this.showSettings.set(false);
  }

  /** SD-2096: open/close the Allo settings drop-down. */
  toggleSettings(): void {
    if (!this.showChat()) return;
    this.showSettings.set(!this.showSettings());
    if (this.showSettings()) this.showHistory.set(false);
  }

  /** SD-2096: open the settings drop-down (no-op when it already is), for the toast's action. */
  openSettings(): void {
    if (!this.showChat() || this.showSettings()) return;
    this.setActiveTab('chat');
    this.toggleSettings();
  }

  /**
   * SD-2661: the history list deleted a chat. Deleting the thread you are reading leaves the
   * panel pointing at nothing, so drop into a fresh chat rather than an empty transcript.
   *
   * `selectedChatId` only covers a thread picked from history. A chat started here is created
   * inside `qt-ai-chat` on the first prompt and lands solely in the store under this session's
   * mysid, so deleting one of those has to be recognised through that map — otherwise the panel
   * keeps the deleted chat open and the next prompt fails with "Chat not found".
   */
  onChatDeleted(chatId: string): void {
    if (this.selectedChatId() === chatId) {
      this.startNewChat();
      return;
    }

    const createdId =
      this.aiChatStore.createdChatIdByMysid()[`chats_${this.chatSessionKey()}`];
    if (createdId === chatId) {
      this.startNewChat();
    }
  }

  selectChat(chatId: string): void {
    if (!this.showChat()) return;
    this.alloPanel.clearPrompt();
    this.showHistory.set(false);
    this.showSettings.set(false);
    this.lazyChatSubscribe.set(false);
    this.selectedChatId.set(chatId);
    this.chatSessionKey.set(this.createWindowId());
    this.setActiveTab('chat');

    this.aiChatStore.getChatEvents({
      mysid: `chatEvent_${chatId}`,
      prms: { chatId },
    });
  }

  private requestChatHistoryIfNeeded(): void {
    const mysid = `chats_${this.chatSessionKey()}`;
    if (this.lastChatHistoryMysid === mysid) {
      return;
    }

    this.aiChatStore.getChats({ mysid });
    this.lastChatHistoryMysid = mysid;
  }

  /** SD-3045: brings the Allo popout to the front. False when it is not open. */
  focusChatPopout(): boolean {
    return this.popoutService.focusPopout(AI_CHAT_POPOUT_WINDOW_NAME);
  }

  openChatInNewWindow(): void {
    if (!this.showChat()) return;

    // No chat selected from history: use the chat created for the current session (e.g. new chat with messages)
    const chatId =
      this.selectedChatId() ??
      this.aiChatStore.createdChatIdByMysid()[
        `chats_${this.chatSessionKey()}`
      ] ??
      null;
    this.openPopoutWithData(chatId);
  }

  private openPopoutWithData(chatId: string | null): void {
    const data = {
      context: 'standalone',
      pathLabel: 'standalone/ai-chat',
      popoutType: 'ai-agent',
      chatId,
      windowId: this.chatSessionKey(),
      startNew: chatId === null,
      lazySubscribe: this.lazyChatSubscribe(),
    };

    this.saveAiChatPopoutState({
      chatId,
      windowId: this.chatSessionKey(),
      startNew: chatId === null,
      lazySubscribe: this.lazyChatSubscribe(),
    });

    this.popoutService.openPopoutModal(
      AiChatPopoutComponent,
      data,
      AI_CHAT_POPOUT_WINDOW_NAME,
      this.viewContainerRef
    );

    this.closePanel();
  }

  addNote(): void {
    this.dialogLoader
      .open('note-dialog', {
        mysid: 'right-sidenav',
        refId: '',
        allTags: this.noteAllTags$.getValue(),
      })
      .subscribe();
  }

  addContact(): void {
    this.newRelationsStore.connectPeople({ mysid: this.mysid });
    this.dialogLoader
      .open('customer-dialog', {})
      .pipe(
        tap(() => {
          this.newRelationsStore.disconnectPeople(this.mysid);
        }),
        filter(result => !!result)
      )
      .subscribe((result: Customer) => {
        if (result?._id) {
          this.redirectToContactList(btoa(result._id));
        }
      });
  }

  public redirectToContactList(id: string): void {
    const targetUrl = `/rels/people/contact/${id}`;
    const prevUrl = this.router.url;

    this.permissions
      .navigateByUrlIfAllowed$(targetUrl, {
        queryParams: { type: 'customer', prevUrl },
      })
      .subscribe();
  }

  ngOnDestroy(): void {
    this.tagManagerStore.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  openTab(tab: 'chat' | 'tasks'): boolean {
    if (tab === 'chat' && !this.showChat()) {
      return false;
    }
    this.setActiveTab(tab);
    if (tab === 'chat') {
      this.syncChatFromPopoutState();
      this.requestChatHistoryIfNeeded();
    }
    return true;
  }

  private setActiveTab(tab: 'chat' | 'tasks') {
    this.selectedTab.set(tab);
    this.activeTabChange.emit(tab);
  }

  private syncChatFromPopoutState(): void {
    const state = this.readAiChatPopoutState();
    if (!state) return;

    this.applyPopoutState(state);
  }

  private applyPopoutState(state: AiChatPopoutState): void {
    const shouldUpdate =
      this.selectedChatId() !== state.chatId ||
      this.lazyChatSubscribe() !== state.lazySubscribe ||
      this.followedPopoutWindowId !== state.windowId;

    if (!shouldUpdate) return;

    // SD-3045: follow the popout's chat, but on a session key of our own. The popout runs in this
    // window on the same store, so on its key both chats would share one `chats_` stream, and
    // destroying the docked chat would unsubscribe it from under the popout.
    this.followedPopoutWindowId = state.windowId;
    this.chatSessionKey.set(this.createWindowId());
    this.lazyChatSubscribe.set(state.lazySubscribe);
    this.selectedChatId.set(state.chatId);

    if (state.chatId) {
      this.aiChatStore.getChatEvents({
        mysid: `chatEvent_${state.chatId}`,
        prms: { chatId: state.chatId },
      });
      return;
    }

    if (state.startNew || state.lazySubscribe) {
      this.requestChatHistoryIfNeeded();
    }
  }

  private restoreAiChatPopoutIfNeeded(): void {
    if (this.popoutRestored || !this.showChat()) return;
    this.popoutRestored = true;

    const state = this.readAiChatPopoutState();
    if (!state) return;

    // SD-3045: never the popout's key, see applyPopoutState.
    this.followedPopoutWindowId = state.windowId;
    this.chatSessionKey.set(this.createWindowId());
    this.lazyChatSubscribe.set(state.lazySubscribe);
    this.selectedChatId.set(state.chatId);

    this.markPopoutRestoreInProgress();

    this.popoutService.openPopoutModal(
      AiChatPopoutComponent,
      {
        context: 'standalone',
        pathLabel: 'standalone/ai-chat',
        popoutType: 'ai-agent',
        chatId: state.chatId,
        windowId: state.windowId,
        startNew: state.startNew,
        lazySubscribe: state.lazySubscribe,
      },
      AI_CHAT_POPOUT_WINDOW_NAME,
      this.viewContainerRef
    );
  }

  private saveAiChatPopoutState(state: AiChatPopoutState): void {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(AI_CHAT_POPOUT_STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage failures (private mode, disabled storage).
    }
  }

  private readAiChatPopoutState(): AiChatPopoutState | null {
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem(AI_CHAT_POPOUT_STORAGE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AiChatPopoutState;
    } catch {
      sessionStorage.removeItem(AI_CHAT_POPOUT_STORAGE_KEY);
      return null;
    }
  }

  private markPopoutRestoreInProgress(): void {
    if (typeof window === 'undefined') return;
    try {
      sessionStorage.setItem(AI_CHAT_POPOUT_RESTORE_KEY, Date.now().toString());
      window.setTimeout(() => {
        try {
          sessionStorage.removeItem(AI_CHAT_POPOUT_RESTORE_KEY);
        } catch {
          // Ignore storage failures during cleanup.
        }
      }, AI_CHAT_POPOUT_RESTORE_TTL_MS);
    } catch {
      // Ignore storage failures (private mode, disabled storage).
    }
  }
}
